import { useEffect } from "react";

/*
 * <Seo /> — imperatively upserts per-route metadata into <head>.
 *
 * React 19's native <title>/<meta name> hoisting is good but doesn't
 * reliably dedupe <link rel="canonical"> or <meta property="og:*">.
 * We update document.head directly so there is exactly one of each.
 *
 * Static tags in public/index.html act as the homepage-baseline for
 * non-JS scrapers (Facebook, LinkedIn); this component takes over the
 * moment JS runs so browsers, Googlebot, and screen readers see the
 * per-route values.
 */
const SITE = "https://carol-lines.com";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function Seo({
  title,
  description,
  path = "/",
  image,
  imageAlt,
  type = "website",
}) {
  const url = `${SITE}${path}`;
  const fullTitle = title
    ? `${title} | The Subject Is the Question`
    : "The Subject Is the Question | Photography by Carol Lines";
  const desc =
    description ||
    "Photography by Carol Lines. Landscapes, wildlife, people and the night sky, connected by the questions they provoke.";
  const ogImage = image || `${SITE}/api/images/p13`;
  const ogImageAlt =
    imageAlt || "A mountain lake held perfectly still, its far shore softened by mist.";

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', { name: "description", content: desc });
    upsertLink("canonical", url);

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "The Subject Is the Question" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: ogImageAlt });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: ogImageAlt });
  }, [fullTitle, desc, url, type, ogImage, ogImageAlt]);

  return null;
}
